import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { LANGUAGES, ALIASES } from '../lib/highlight';
import { fileIdOf, isVideo, markdownUrlTransform, useStoredFile } from '../lib/files';

/**
 * One attachment: the image (a link to the original, in a new tab), a <video> with its
 * poster, a "Processing…" chip while Transforge works (polled every 3 s), or "file
 * unavailable" when the viewer cannot see it / it failed. `inEditor` drops the link so a
 * click selects the node instead of navigating.
 */
export function FileEmbed({ id, alt, inEditor }: { id: string; alt?: string; inEditor?: boolean }) {
  const entry = useStoredFile(id);
  if (!entry) {
    return (
      <span className="file-chip" data-testid="file-loading" data-file-id={id}>
        <span className="file-spin" aria-hidden="true" />
        Loading {alt || 'file'}…
      </span>
    );
  }
  if ('unavailable' in entry || entry.file.status === 'failed' || (entry.file.status === 'ready' && !entry.file.url)) {
    return (
      <span className="file-chip file-chip--bad" data-testid="file-unavailable" data-file-id={id} title={alt || undefined}>
        file unavailable{alt ? `: ${alt}` : ''}
      </span>
    );
  }
  const f = entry.file;
  if (f.status !== 'ready' || !f.url) {
    return (
      <span className="file-chip" data-testid="file-processing" data-file-id={id} role="status">
        <span className="file-spin" aria-hidden="true" />
        Processing {f.filename}…
      </span>
    );
  }
  if (isVideo(f)) {
    return (
      <video
        className="file-video"
        data-testid="file-video"
        data-file-id={id}
        src={f.url}
        poster={f.posterUrl ?? undefined}
        controls
        preload="metadata"
        playsInline
        aria-label={alt || f.filename}
      />
    );
  }
  const img = <img className="file-img" data-testid="file-image" data-file-id={id} src={f.url} alt={alt || f.filename} loading="lazy" />;
  if (inEditor) return img;
  return (
    <a href={f.url} target="_blank" rel="noreferrer noopener" className="file-img-link" title={`Open ${f.filename}`}>
      {img}
    </a>
  );
}

/** Minimal mdast shape — enough for the mention pass without a direct mdast dependency. */
interface MdNode {
  type: string;
  value?: string;
  url?: string;
  children?: MdNode[];
  data?: { hName?: string; hProperties?: Record<string, unknown> };
}

/** `@<email>` as the editor serializes a mention (domain may be dotless, e.g. `dev@localhost`). */
const MENTION_RE = /(^|[^\w@.])@([\w.%+-]+@[\w-]+(?:\.[\w-]+)*)/g;

const chip = (email: string): MdNode => ({
  type: 'mention',
  data: { hName: 'span', hProperties: { className: ['mention-chip'] } },
  children: [{ type: 'text', value: `@${email}` }],
});

function splitText(value: string): MdNode[] {
  const out: MdNode[] = [];
  let last = 0;
  for (const m of value.matchAll(MENTION_RE)) {
    const start = m.index + m[1].length;
    if (start > last) out.push({ type: 'text', value: value.slice(last, start) });
    out.push(chip(m[2]));
    last = start + 1 + m[2].length;
  }
  if (!out.length) return [{ type: 'text', value }];
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) });
  return out;
}

/**
 * remark pass: `@email` in prose → the same `.mention-chip` the editor shows. Code
 * (`code`/`inlineCode` carry `value`, no text children) and links are never entered. A
 * dotted email may already be a GFM autolink (`@` text + `mailto:` link) — that pair is
 * folded back into one chip.
 */
function remarkMentions() {
  const walk = (node: MdNode) => {
    if (!node.children || node.type === 'link' || node.type === 'linkReference') return;
    const next: MdNode[] = [];
    for (const child of node.children) {
      const prev = next[next.length - 1];
      const label = child.children?.length === 1 ? child.children[0].value : undefined;
      if (
        child.type === 'link' &&
        label &&
        child.url === `mailto:${label}` &&
        prev?.type === 'text' &&
        /(^|[^\w@.])@$/.test(prev.value ?? '')
      ) {
        prev.value = prev.value!.slice(0, -1);
        if (!prev.value) next.pop();
        next.push(chip(label));
        continue;
      }
      if (child.type === 'text') next.push(...splitText(child.value ?? ''));
      else {
        walk(child);
        next.push(child);
      }
    }
    node.children = next;
  };
  return (tree: MdNode) => walk(tree);
}

/**
 * Safe GFM markdown renderer. react-markdown does NOT render raw HTML (no
 * rehype-raw here), so user-authored notes/comments can't inject markup — the
 * XSS-safe default. remark-gfm adds tables, task-lists (read-only checkboxes),
 * strikethrough, and autolinks (the GitHub/GitLab-flavored subset); `@email`
 * mentions render as the editor's chip. rehype-highlight syntax-colors fenced code
 * blocks using the SAME curated grammar set as the editor (so a ```json block reads
 * identically here and in the composer). Wrapped in `.md-body` for element styling
 * (globals.css). Links open in a new tab.
 */
export function Markdown({ children }: { children: string }) {
  return (
    <div className="md-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMentions]}
        rehypePlugins={[
          [
            rehypeHighlight,
            {
              languages: LANGUAGES,
              aliases: ALIASES,
              ignoreMissing: true,
              detect: false,
            },
          ],
        ]}
        urlTransform={markdownUrlTransform}
        components={{
          a: ({ node: _n, ...props }) => <a {...props} target="_blank" rel="noreferrer noopener" />,
          img: ({ node: _n, src, alt, ...props }) => {
            const id = typeof src === 'string' ? fileIdOf(src) : null;
            if (id) return <FileEmbed id={id} alt={alt} />;
            return <img {...props} src={src} alt={alt} loading="lazy" />;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
